import * as Color from "../Color";
import { ButtonAppearance } from "../Button";

/**
 * Defines a CSS custom property and its fallback color value.
 */
type TokenColorVar = Readonly<{
  propertyName: string;
  fallback: Color.RgbColor;
}>;

/**
 * Creates a token color variable descriptor with a parsed fallback color.
 */
function createTokenColorVar(propertyName: string, fallbackHex: string): TokenColorVar {
  return {
    propertyName,
    fallback: Color.fromString(fallbackHex),
  };
}

/* A badge always renders the light neutral tag style, whose tokens are still named for the "info" group. */
const normalColorVar = createTokenColorVar("--badge-type-info-color-close_icon_fill", "#3e5d6f");
const hoverColorVar = createTokenColorVar("--badge-type-info-color-close_icon_fill_hover", "#56819a");

/**
 * Parses a CSS color string into an RGB color object.
 */
function parseCssColor(colorValue?: string): Color.RgbColor | undefined {
  if (!colorValue) return undefined;
  try {
    return Color.fromString(colorValue);
  } catch {
    return undefined;
  }
}

/**
 * Reads a CSS custom property from the document root.
 */
function readCssCustomProperty(propertyName: string): string | undefined {
  if (typeof window === "undefined" || typeof document === "undefined") return undefined;
  const value = window.getComputedStyle(document.documentElement).getPropertyValue(propertyName).trim();
  return value.length > 0 ? value : undefined;
}

/**
 * Resolves a token color from CSS and falls back to its configured static color.
 */
function resolveTokenColor(tokenColorVar: TokenColorVar): Color.RgbColor {
  const tokenValue = readCssCustomProperty(tokenColorVar.propertyName);
  return parseCssColor(tokenValue) ?? tokenColorVar.fallback;
}

/**
 * Builds ButtonAppearance for the badge close button.
 */
export function getBadgeCloseButtonAppearance(): ButtonAppearance {
  const normal = resolveTokenColor(normalColorVar);
  const hover = resolveTokenColor(hoverColorVar);

  return {
    padding: { horizontal: 0, vertical: 0 },
    label: {
      letterSpacing: 0,
      uppercase: false,
      weight: "regular",
      fontSize: 16,
      lineHeight: 16,
    },
    icon: {
      size: "regular",
    },
    normal: {
      backgroundColor: Color.Transparent,
      foregroundColor: normal,
      borderColor: Color.Transparent,
    },
    hover: {
      backgroundColor: Color.Transparent,
      foregroundColor: hover,
      borderColor: Color.Transparent,
    },
    pressed: {
      backgroundColor: Color.Transparent,
      foregroundColor: hover,
      borderColor: Color.Transparent,
    },
    disabled: {
      backgroundColor: Color.Transparent,
      foregroundColor: normal,
      borderColor: Color.Transparent,
    },
  };
}
